/**
 * Pagina della partita (V18).
 *
 * Una sola rotta che cambia forma con lo stato: dati, convocati e formazione
 * dentro il guscio prima del fischio, Live Timer a schermo intero dalla
 * formazione confermata alla fine, tabellino a partita conclusa, dati e
 * ripristino a partita annullata.
 */

import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { TipoEvento } from '@calcio/shared';
import { puo } from '@calcio/shared';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { BanIcon, InfoIcon, PencilIcon, RotateCcwIcon, TimerOffIcon } from 'lucide-react';
import { chiama } from '../api/client.js';
import { messaggio } from '../comuni/dati.js';
import {
  AzioniTestata,
  MenuAzioni,
  PulsantePrincipale,
  type VoceAzione,
} from '../comuni/Azioni.js';
import { Corpo } from '../comuni/Corpo.js';
import { Testata } from '../comuni/Testata.js';
import { useContesto } from '../navigazione/contesto.js';
import { Guscio } from '../navigazione/Guscio.js';
import { VistaStato } from '../navigazione/NonTrovata.js';
import { DialogPartita } from '../partite/DialogPartita.js';
import { PartitaConclusa } from '../partite/PartitaConclusa.js';
import { InfoPartita } from '../partite/InfoPartita.js';
import { rigaSopraPartita } from '../partite/partita.js';
import { Formazione } from './Formazione.js';
import { SchermataTimer } from './SchermataTimer.js';
import type { GiocatoreInRosa } from './tipi.js';
import { type Domanda, useConferma } from '../comuni/Conferma.js';

interface Dettaglio {
  partita: {
    id: string;
    squadraStagioneId: string;
    stato: 'programmata' | 'formazione' | 'in_corso' | 'conclusa' | 'annullata';
    avversario: string;
    inCasa: boolean;
    luogo: string | null;
    note: string | null;
    competizione: string;
    dataOra: string;
    nPeriodi: number;
    durataPeriodoMin: number;
    giocatoriInCampo: number;
  };
  tipiEventoAttivi: TipoEvento[];
  rosa: GiocatoreInRosa[];
  /** Avviata col timer almeno una volta: senza, la partita non si riapre. */
  cronometrata: boolean;
}

export function PaginaPartita() {
  const { ss, id } = useParams<{ ss?: string; id: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { utente } = useContesto();
  const [dettaglio, setDettaglio] = useState<Dettaglio | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [modifica, setModifica] = useState(false);
  const [chiedi, conferma] = useConferma();

  const carica = useCallback(async () => {
    if (!id) return;
    try {
      setDettaglio(await chiama<Dettaglio>(`/partite/${id}`));
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Partita non disponibile.');
    }
  }, [id]);

  useEffect(() => {
    void carica();
  }, [carica]);

  if (errore)
    return (
      <VistaStato
        titolo="Partita non disponibile"
        uscita={{ a: ss ? `/sq/${ss}/partite` : '/', etichetta: 'Torna alle partite' }}
      >
        {errore}
      </VistaStato>
    );
  // Passando da una partita all'altra il dettaglio resta quello vecchio fino
  // alla risposta: usarlo porterebbe il controllo sotto sulla squadra sbagliata.
  if (!dettaglio || !id || dettaglio.partita.id !== id) {
    return <p className="attesa">Carico la partita…</p>;
  }

  const { partita, rosa, tipiEventoAttivi } = dettaglio;

  // Il contesto nell'URL deve essere quello della partita: un link vecchio
  // senza prefisso, o con la squadra sbagliata, si corregge da solo.
  if (partita.squadraStagioneId !== ss) {
    return <Navigate to={`/sq/${partita.squadraStagioneId}/partite/${partita.id}`} replace />;
  }

  // Le schede e la modifica sono della partita conclusa: prima, e riaperta, c'è la pagina.
  const base = `/sq/${ss}/partite/${id}`;
  if (partita.stato !== 'conclusa' && pathname.replace(/\/$/, '') !== base) {
    return <Navigate to={base} replace />;
  }

  const gestisce = puo(utente.assegnazioni, 'partita.gestisci', ss);
  const opera = puo(utente.assegnazioni, 'timer.opera', ss);

  const agisci = async (percorso: string, domanda: Domanda, esito: string) => {
    if (!(await chiedi(domanda))) return;
    try {
      await chiama(percorso, { metodo: 'POST' });
      await carica();
      toast.success(esito);
    } catch (e) {
      toast.error(messaggio(e));
    }
  };

  if (partita.stato === 'annullata') {
    return (
      <Guscio>
        {conferma}
        <DatiPartita
          indietro={ss ? `/sq/${ss}` : '/'}
          partita={partita}
          principale={
            gestisce && (
              <PulsantePrincipale
                etichetta="Ripristina"
                Icona={RotateCcwIcon}
                onClick={() =>
                  void agisci(
                    `/partite/${id}/ripristina`,
                    {
                      titolo: 'Ripristinare la partita?',
                      testo: 'Torna programmata, con i convocati di prima.',
                      azione: 'Ripristina',
                    },
                    'Partita ripristinata.',
                  )
                }
              />
            )
          }
        />
      </Guscio>
    );
  }

  if (partita.stato === 'conclusa') {
    return (
      <Guscio>
        <PartitaConclusa
          ss={ss}
          partita={partita}
          cronometrata={dettaglio.cronometrata}
          onRiaperta={carica}
        />
      </Guscio>
    );
  }

  // Prima del fischio d'inizio si scelgono convocati e titolari, con il menu
  // a disposizione. Salvata la formazione lo stato passa a 'formazione' e il
  // comando "Inizia la partita" vive dentro SchermataTimer, non qui: e li
  // che si vede il cronometro fermo sull'offset zero prima del fischio.
  if (partita.stato === 'programmata') {
    return (
      <Guscio>
        {conferma}
        <DatiPartita
          indietro={ss ? `/sq/${ss}` : '/'}
          partita={partita}
          principale={
            gestisce && (
              <PulsantePrincipale
                etichetta="Modifica"
                Icona={PencilIcon}
                onClick={() => setModifica(true)}
              />
            )
          }
          voci={[
            // Giocata senza timer: si chiude, poi si ricostruisce dagli eventi.
            gestisce &&
              opera &&
              new Date(partita.dataOra).getTime() <= Date.now() && {
                etichetta: 'Giocata senza timer',
                Icona: TimerOffIcon,
                onSelect: () =>
                  void agisci(
                    `/partite/${id}/chiudi-senza-timer`,
                    {
                      titolo: 'Chiudere la partita come giocata senza timer?',
                      testo:
                        'I tempi valgono la durata regolamentare; titolari, cambi e gol si inseriscono poi dagli eventi. Non si potrà riaprire.',
                      azione: 'Chiudi la partita',
                    },
                    'Partita chiusa: ora inserisci gli eventi.',
                  ),
              },
            gestisce && {
              etichetta: 'Annulla la partita',
              Icona: BanIcon,
              onSelect: () =>
                void agisci(
                  `/partite/${id}/annulla`,
                  {
                    titolo: 'Annullare la partita?',
                    testo: 'Resta in calendario come annullata; si può ripristinare.',
                    azione: 'Annulla la partita',
                  },
                  'Partita annullata.',
                ),
            },
          ]}
        />
        {gestisce && (
          <DialogPartita
            ss={ss!}
            aperto={modifica}
            partitaId={id}
            onChiudi={() => setModifica(false)}
            onSalvato={carica}
          />
        )}
        <Corpo>
          {/* L'Admin legge: convocati e formazione li scelgono Mister e Dirigente. */}
          <Formazione
            partitaId={id}
            squadraStagioneId={ss}
            rosa={rosa}
            giocatoriInCampo={partita.giocatoriInCampo}
            modifica={gestisce}
            onConfermata={() => void carica()}
          />
        </Corpo>
      </Guscio>
    );
  }

  return (
    <SchermataTimer
      partitaId={id}
      rosa={rosa}
      tipiAttivi={tipiEventoAttivi}
      onEsci={() => navigate(`/sq/${ss}/partite`)}
      onFuoriDalCampo={carica}
    />
  );
}

function DatiPartita({
  partita,
  principale,
  voci = [],
  indietro,
}: {
  partita: Dettaglio['partita'];
  principale?: ReactNode;
  voci?: VoceAzione[];
  indietro: string;
}) {
  // Competizione, luogo, formato e note stanno nel dialog delle informazioni.
  const [info, setInfo] = useState(false);
  return (
    <Testata
      sopra={rigaSopraPartita(partita)}
      titolo={partita.avversario}
      azione={
        <AzioniTestata>
          {principale}
          <MenuAzioni
            voci={[
              { etichetta: 'Informazioni', Icona: InfoIcon, onSelect: () => setInfo(true) },
              ...voci,
            ]}
          />
          <InfoPartita
            partita={partita}
            formato={partita}
            aperto={info}
            onChiudi={() => setInfo(false)}
          />
        </AzioniTestata>
      }
      indietro={indietro}
    >
      {partita.stato === 'annullata' && (
        <div>
          <Badge variant="destructive">Annullata</Badge>
        </div>
      )}
    </Testata>
  );
}
